/* eslint-disable no-param-reassign */
import React, { useState } from 'react'
import Image from '../../../../helpers/Image/Image'
import { agnostifyUrl } from '../../../../helpers/utils'
import { defaultImgParams } from '../../../../helpers/ComponentHelper'
import { createGolContentDataAttributes } from '../../../../helpers/tracking'
import { useClientSide, useInterval } from '../../../../helpers/hooks'
import {
  StyledContainer,
  StyledContentWrapper,
  StyledClock,
  StyledCountdown,
  StyledGroup,
  StyledCountdownTime,
  StyledCountdownLabel,
  StyledCTAContainer,
  StyledCTA,
  StyledSponsorText,
  StyledSponsorLogo
} from './M0012.styles'
import { M0012Props } from './types'

/**
 * - M001.2 is a hero component  with H1 and H3 headers for big sale events.
 * - It is to be used on top of a page to highlight the purpose of that page.
 * - It can be authored with or without an image, background and text color, countdown, sponsor logo and copy, heading and subheading.
 * */
const M0012 = ({
  sponsorLogoAltText,
  backgroundImageAltText,
  heading,
  isSecondaryHeading = false,
  textColor,
  subheading,
  backgroundImageUrl,
  image,
  sponsorCopy,
  bodyCopy,
  sponsorLogo,
  logo,
  backgroundColor = 'white',
  countdownTime = null,
  height = 'default',
  linkTarget,
  linkTitle,
  linkUrl = '',
  ctaCopy,
  link,
  consumingGridType = 'Isolated',
  onLinkClick,
  positionInGrid,
  imageFormat
}: M0012Props): React.ReactElement => {
  const clientImage = {
    backgroundImage: useClientSide(`url('${image?.url || backgroundImageUrl}${defaultImgParams}?w=1200')`)
  }
  const isClientSide = clientImage.backgroundImage.length > 0

  const initialTimerState = { days: 0, hours: 0, min: 0, sec: 0 }
  const [countdown, setCountdown] = useState(initialTimerState)
  const { days, hours, min, sec } = countdown
  const isCountdownFinished = min === 0 && sec < 0

  const Heading = isSecondaryHeading ? 'h2' : 'h1'

  const url = link?.url || linkUrl
  const title = link?.title || linkTitle
  const target = link?.target || linkTarget
  const copy = link?.copy || ctaCopy

  useInterval(
    () => {
      const timeLeft = initialTimerState
      let dif = countdownTime - Date.parse(new Date().toString()) / 1000
      if (dif >= 86400) {
        timeLeft.days = Math.floor(dif / 86400)
        dif -= timeLeft.days * 86400
      }
      if (dif >= 3600) {
        timeLeft.hours = Math.floor(dif / 3600)
        dif -= timeLeft.hours * 3600
      }
      if (dif >= 60) {
        timeLeft.min = Math.floor(dif / 60)
        dif -= timeLeft.min * 60
      }
      timeLeft.sec = dif
      setCountdown(timeLeft)
    },
    1000,
    isCountdownFinished
  )
  const logoUrl = logo?.url || sponsorLogo
  const sponsorBodyCopy = bodyCopy || sponsorCopy
  const sponsorLogoStyle = logoUrl ? { backgroundImage: `url('${logoUrl}?w=200')` } : null
  const addZeros = (value) => {
    value = String(value)
    if (value.length === 1) return `0${value}`
    return value
  }
  const pluralize = (value) => (value !== 1 ? 's' : '')
  const validSubheading = subheading && subheading.replace(/\s/g, '').length > 0
  const expiredOrActive = min === 0 && sec < 1 ? 'expired' : 'active'

  const getGOLDataAttributes = (action?: string) =>
    createGolContentDataAttributes(
      consumingGridType,
      `M0012 - ${positionInGrid !== undefined ? `Position${positionInGrid}` : 'NA'}`,
      title,
      url,
      heading,
      action
    )

  const addGolTracking = (evt) => {
    const dataAttributes = getGOLDataAttributes()
    Object.keys(dataAttributes).forEach((attribute) =>
      evt.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (evt) => {
    if (onLinkClick) {
      onLinkClick(evt, agnostifyUrl(url))
    }
    addGolTracking(evt)
  }

  return (
    <StyledContainer
      {...getGOLDataAttributes('Load')}
      height={height}
      textColor={textColor}
      backgroundColor={backgroundColor}
      data-test='m0012-container'
      tabIndex={url && 0}
      as={!url ? 'div' : undefined}
      href={url ? agnostifyUrl(url) : undefined}
      target={url && target ? target : undefined}
      title={url && title ? title : undefined}
      onClick={(evt) => url && ctaClick(evt)}
    >
      {(image?.url || backgroundImageUrl) && (
        <Image
          imageFormat={imageFormat}
          src={image?.url || backgroundImageUrl}
          data-test='m0012-image'
          alt={image?.altText || backgroundImageAltText}
        />
      )}
      <StyledContentWrapper data-test='m0012-content-container' textColor={textColor}>
        <Heading data-test='m0012-heading'>{heading}</Heading>
        {validSubheading && <h4 data-test='m0012-subheading'>{subheading}</h4>}
        {!!countdownTime && sec >= 0 && (
          <StyledClock>
            <StyledCountdown expiredOrActive={expiredOrActive} data-test='m0012-countdown' role='timer'>
              {!!days && (
                <StyledGroup data-test='m0012-countdown-days'>
                  <StyledCountdownTime data-test='m0012-countdown-days-value'>
                    {isClientSide ? addZeros(days) : '00'}
                  </StyledCountdownTime>
                  <StyledCountdownLabel data-test='m0012-countdown-days-label'>
                    Day
                    {pluralize(days)}
                  </StyledCountdownLabel>
                </StyledGroup>
              )}
              {!!hours && (
                <StyledGroup data-test='m0012-countdown-hours'>
                  <StyledCountdownTime data-test='m0012-countdown-hours-value'>
                    {isClientSide ? addZeros(hours) : '00'}
                  </StyledCountdownTime>
                  <StyledCountdownLabel data-test='m0012-countdown-hours-label'>
                    Hr
                    {pluralize(hours)}
                  </StyledCountdownLabel>
                </StyledGroup>
              )}
              {!!min && (
                <StyledGroup data-test='m0012-countdown-minutes'>
                  <StyledCountdownTime data-test='m0012-countdown-minutes-value'>
                    {isClientSide ? addZeros(min) : '00'}
                  </StyledCountdownTime>
                  <StyledCountdownLabel data-test='m0012-countdown-minutes-label'>Min</StyledCountdownLabel>
                </StyledGroup>
              )}
              <StyledGroup data-test='m0012-countdown-seconds'>
                <StyledCountdownTime data-test='m0012-countdown-seconds-value'>
                  {isClientSide ? addZeros(sec) : '00'}
                </StyledCountdownTime>
                <StyledCountdownLabel data-test='m0012-countdown-seconds-label'>Sec</StyledCountdownLabel>
              </StyledGroup>
            </StyledCountdown>
          </StyledClock>
        )}
        {url && copy && title && (
          <StyledCTAContainer>
            <StyledCTA data-test='m0012-cta'>{copy}</StyledCTA>
          </StyledCTAContainer>
        )}
        {sponsorBodyCopy && logoUrl && (
          <>
            <StyledSponsorText data-test='m0012-sponsor-copy' textColor={textColor}>
              {sponsorBodyCopy}
            </StyledSponsorText>
            <StyledSponsorLogo
              data-test='m0012-sponsor-logo'
              alt={logo?.altText || sponsorLogoAltText}
              style={sponsorLogoStyle}
            />
          </>
        )}
      </StyledContentWrapper>
    </StyledContainer>
  )
}

export default M0012
